import React, { useEffect, useRef, useState } from "react";
import { Plus, Minus } from "lucide-react";

function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (setInView(true), io.disconnect())),
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
}

const reveal = (inView, delay = 0) => ({
  opacity: inView ? 1 : 0,
  transform: inView ? "translateY(0)" : "translateY(12px)",
  transition: `opacity 480ms ease ${delay}ms, transform 480ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
});

const body = "rgba(26,26,25,0.78)";

const FAQS = [
  {
    q: "When will my order be shipped?",
    a: "To ensure every piece meets our quality standards, orders are typically processed and dispatched within 2–5 business days.",
  },
  {
    q: "How long does delivery take?",
    a: "Once dispatched, delivery is typically completed within 7 business days, depending on the delivery location.",
  },
  {
    q: "How do I choose my size?",
    a: "Please refer to our Size Guide for detailed measurements and fit recommendations.",
  },
  {
    q: "What if I'm between sizes?",
    a: "If you're between sizes, we recommend choosing the larger size for a more comfortable fit.",
  },
  {
    q: "Can I exchange my item?",
    a: "We offer size exchanges on eligible items, subject to availability. Please refer to our Exchange & Returns Policy for full details.",
  },
  {
    q: "Can I return my order?",
    a: "Please refer to our Exchange & Returns Policy for information regarding eligibility and timelines.",
  },
  {
    q: "How should I care for my garment?",
    a: "We recommend following the care instructions provided with your garment to preserve its quality, shape, and finish.",
  },
];

function AccordionItem({ q, a, isOpen, onToggle, idx }) {
  const contentRef = useRef(null);
  const [maxH, setMaxH] = useState(0);

  useEffect(() => {
    if (!contentRef.current) return;
    setMaxH(isOpen ? contentRef.current.scrollHeight : 0);
  }, [isOpen]);

  return (
    <div className="border-b" style={{ borderColor: "rgba(26,26,25,0.10)" }} data-testid={`faq-item-${idx}`}>
      <button
        onClick={onToggle}
        className="w-full py-7 sm:py-8 flex justify-between items-center text-left gap-6 group"
        aria-expanded={isOpen}
        data-testid={`faq-toggle-${idx}`}
      >
        <span
          className="font-display"
          style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
            fontWeight: 400,
            letterSpacing: "-0.005em",
            color: "#0E0E0D",
          }}
        >
          {q}
        </span>
        <span className="flex-shrink-0 opacity-60 transition-opacity group-hover:opacity-100">
          {isOpen ? <Minus size={18} strokeWidth={1.2} /> : <Plus size={18} strokeWidth={1.2} />}
        </span>
      </button>
      <div
        style={{
          maxHeight: `${maxH}px`,
          overflow: "hidden",
          transition: "max-height 380ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
        data-testid={`faq-answer-${idx}`}
      >
        <div
          ref={contentRef}
          className="pb-8 pr-10 text-base sm:text-[16.5px] leading-[1.85]"
          style={{
            color: body,
            opacity: isOpen ? 1 : 0,
            transition: "opacity 300ms ease 80ms",
            maxWidth: "640px",
          }}
        >
          {a}
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [topRef, topInView] = useInView();
  const [openIdx, setOpenIdx] = useState(-1); // all collapsed by default

  return (
    <main
      ref={topRef}
      className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 pt-20 sm:pt-28 pb-24 sm:pb-32"
      data-testid="faq-page"
    >
      {/* Header */}
      <div className="max-w-[820px]">
        <h1
          className="font-display leading-[1.05]"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.25rem)",
            letterSpacing: "-0.005em",
            fontWeight: 400,
            ...reveal(topInView, 0),
          }}
          data-testid="faq-heading"
        >
          F<span className="italic">A</span>Q
        </h1>
        <p
          className="font-display italic mt-10 sm:mt-12 leading-[1.6]"
          style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
            color: "#1A1A19",
            maxWidth: "640px",
            ...reveal(topInView, 160),
          }}
          data-testid="faq-intro"
        >
          Find answers to common questions about sizing, shipping, exchanges, and your Tamoré order.
        </p>
      </div>

      {/* Accordion */}
      <div className="mt-20 sm:mt-24 max-w-[860px]" style={reveal(topInView, 320)}>
        {FAQS.map((item, idx) => (
          <AccordionItem
            key={idx}
            idx={idx}
            q={item.q}
            a={item.a}
            isOpen={openIdx === idx}
            onToggle={() => setOpenIdx(openIdx === idx ? -1 : idx)}
          />
        ))}
      </div>
    </main>
  );
}
